import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import {
    DefaultGroupsNames,
    IGroupEntityDTO,
    ITemplateEntityDTO,
    ITemplateGroupEntityDTO,
    SortedTemplateItem,
    TemplateType,
} from '@modeso/types__aumico-lib-templates-be';
import { FinancialLineItemType } from '@modeso/types__aumico-lib-settings-be';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import Debug from 'debug';
import { cloneDeep } from 'lodash-es';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import Swal from 'sweetalert2';
import { EMPTY_ARRAY_LENGTH } from '../../../../../aumico-lib-core-fe/src/lib';
import { BaseComponent } from '../../components/base.component';
import { BALANCE_DEFAULT_GROUPS } from '../../constants/default-groups.constant';
import { SortingTemplateItemsHelper } from '../../helpers/sorting-mapper.helper';
import { IFinancialLineItem, ISettings } from '../../interfaces/settings.interface';
import { TemplateGroup } from '../../models/template.model';
import { TemplatesProvider } from '../../provider/templates.provider';

const debug = Debug('aumico:aumico-lib-templates-fe:TemplatesFormComponent');
@UntilDestroy()
@Component({
    selector: 'aumico-templates-form',
    templateUrl: 'templates-form.component.html',
    styleUrls: ['templates-form.component.scss'],
})
export class TemplatesFormComponent extends BaseComponent implements OnInit, OnDestroy {
    @Input() settings: ISettings;
    @Input() financialLineItems: [];
    @Input() currentLanguage = 'de-ch';
    @Input() languages: string[];
    @Output() directToTemplatesListEvent = new EventEmitter();

    template: ITemplateEntityDTO = {} as ITemplateEntityDTO;
    TemplateType = TemplateType;
    fliOptions: string[] = [];
    equityGroupOptions: string[] = [];
    isEditMode: Boolean = false;
    templateGroups: IGroupEntityDTO[] = [];
    templateFinancialLineItems: IFinancialLineItem[] = [];

    // Form Controls
    templateNameControl = new UntypedFormControl();
    templateAccountBasisControl = new UntypedFormControl();
    templateLegalFormControl = new UntypedFormControl();

    constructor(public override templatesProvider: TemplatesProvider) {
        super(templatesProvider);
    }

    override ngOnInit() {
        debug('ngOnInit');
        super.ngOnInit();
        debug('dispatch groups');
        this.templatesProvider.dispatchGroups();
        this.templatesProvider.setFinancialLineItems(this.financialLineItems);
        this.getActiveTemplateType();
        this.getActiveTemplate();
        this.getFinancialLineItems(this.template.type);
    }

    getActiveTemplateType() {
        this.subscriptions.push(
            this.templatesProvider
                .getActiveTemplateType$()
                .pipe(untilDestroyed(this))
                .subscribe((activeTemplateType: TemplateType) => {
                    if (activeTemplateType) {
                        debug(activeTemplateType);
                        this.template.type = activeTemplateType;
                    }
                }),
        );
    }

    getFinancialLineItems(templateType: TemplateType) {
        this.subscriptions.push(
            this.templatesProvider.getFinancialLineItems$().subscribe((financialLineItems: IFinancialLineItem[]) => {
                if (financialLineItems) {
                    if (templateType == TemplateType.BALANCE) {
                        this.templateFinancialLineItems = financialLineItems.filter(
                            (fli) =>
                                fli.type == FinancialLineItemType.ASSETS ||
                                fli.type == FinancialLineItemType.LIABILITIES,
                        );
                    } else if (templateType == TemplateType.INCOME) {
                        this.templateFinancialLineItems = financialLineItems.filter(
                            (fli) =>
                                fli.type == FinancialLineItemType.EXPENSES || fli.type == FinancialLineItemType.INCOME,
                        );
                    }
                }
            }),
        );
    }

    getActiveTemplate() {
        this.subscriptions.push(
            this.templatesProvider.getActiveTemplate$().subscribe((activeTemplate: ITemplateEntityDTO) => {
                if (activeTemplate != null) {
                    debug(activeTemplate);
                    this.isEditMode = true;
                    this.template = activeTemplate;
                    this.template.group = SortingTemplateItemsHelper.mapGroupSortedItemsToSortedItem(
                        activeTemplate.group,
                    );
                    this.setTemplateGroupsAndItems(this.template.group);

                    if (!this.equityGroupOptions.includes(this.template.equityGroupName)) {
                        this.template.equityGroupName = undefined;
                    }
                } else {
                    this.isEditMode = false;
                    this.setDefaultTemplateGroups();
                }
                if (this.template.type == TemplateType.BALANCE) {
                    this.getFliOptions();
                }
            }),
        );
    }

    //For Edit Mode
    setTemplateGroupsAndItems(templateGroup: ITemplateGroupEntityDTO) {
        if (templateGroup.items.length) {
            let selectedFinancialLineItems: string[] = [];
            let subtotalsNames: string[] = [];
            for (let item of templateGroup.items) {
                if (item.isSubtotal) {
                    subtotalsNames.push(item.name);
                } else {
                    selectedFinancialLineItems.push(item.name);
                }
            }
            this.templatesProvider.setSubtotalsNames(subtotalsNames);
            this.templatesProvider.selectFinancialLineItems(selectedFinancialLineItems);
        }
        if (templateGroup.groups.length) {
            let selectedGroupsIds: string[] = [];
            for (let group of templateGroup.groups) {
                selectedGroupsIds.push(group.groupRef._id);
                this.equityGroupOptions.push(group.groupRef.name);
                this.setTemplateGroupsAndItems(group);
            }
            this.templatesProvider.selectGroups(selectedGroupsIds);
        }
    }

    // For Add Mode
    setDefaultTemplateGroups() {
        this.subscriptions.push(
            this.templatesProvider
                .getDefaultGroups$()
                .pipe(take(2))
                .subscribe((defaultGroups: Map<string, IGroupEntityDTO>) => {
                    if (defaultGroups != null) {
                        if (this.template.type === TemplateType.BALANCE) {
                            const balanceSheetDefaultGroup = defaultGroups.get(DefaultGroupsNames.BALANCE_SHEET);
                            this.template.group = new TemplateGroup(balanceSheetDefaultGroup);
                            for (let group of BALANCE_DEFAULT_GROUPS) {
                                const defaultGroup = defaultGroups.get(group);
                                const templateGroup = new TemplateGroup(defaultGroup);
                                this.template.group.groups.push(templateGroup);
                                (this.template.group.sortedItems as SortedTemplateItem[]).push(templateGroup);
                            }
                        } else if (this.template.type === TemplateType.INCOME) {
                            const profitOfTheYearDefaultGroup = defaultGroups.get(
                                DefaultGroupsNames.PROFIT_OF_THE_YEAR,
                            );
                            this.template.group = new TemplateGroup(profitOfTheYearDefaultGroup);
                        }
                        debug(this.template.group);
                    }
                }),
        );
    }

    getFliOptions() {
        this.subscriptions.push(
            this.templatesProvider
                .getCashAndProfitOptions$()
                .pipe(untilDestroyed(this))
                .subscribe((fliOptions: string[]) => {
                    if (fliOptions.length !== EMPTY_ARRAY_LENGTH) {
                        this.fliOptions = fliOptions;
                        //In case selected fli is removed
                        if (!this.fliOptions.includes(this.template.cashAndCash)) this.template.cashAndCash = null;

                        if (!this.fliOptions.includes(this.template.profitOfTheYear))
                            this.template.profitOfTheYear = null;

                        if (!this.fliOptions.includes(this.template.uncoveredShortfallAssetsFli)) {
                            this.template.uncoveredShortfallAssetsFli = null;
                        }

                        if (!this.fliOptions.includes(this.template.uncoveredShortfallLiabilitiesFli)) {
                            this.template.uncoveredShortfallLiabilitiesFli = null;
                        }

                        if (!this.fliOptions.includes(this.template.annualSurplusFliName)) {
                            this.template.annualSurplusFliName = null;
                        }
                    }
                }),
        );
    }

    isValidTemplateGroup(templateGroup: TemplateGroup) {
        // Case1: group with no items, no groups
        if (!templateGroup.items.length && !templateGroup.groups.length) {
            Swal.fire({
                icon: 'error',
                text: 'Group' + ' "' + templateGroup.groupRef.title[this.currentLanguage] + '" ' + 'is empty',
            });
            return false;
        }
        // Case2: group with items, no groups
        else if (templateGroup.items.length && !templateGroup.groups.length) {
            return true;
        }
        // Case3: group with groups, items or no items
        else if (templateGroup.groups.length) {
            for (let group of templateGroup.groups) {
                if (!this.isValidTemplateGroup(group)) {
                    return false;
                }
            }
            return true;
        }
        return false;
    }

    checkTemplateNameExists() {
        this.subscriptions.push(
            this.templatesProvider
                .getTemplateNameExists$(this.template.name, this.template._id)
                .pipe(take(1))
                .subscribe((templateNameExist: boolean) => {
                    if (templateNameExist) {
                        this.templateNameControl.setErrors({ templateNameExists: true });
                    }
                }),
        );
    }

    checkTemplateAccountBasisAndLegalFormExists() {
        this.templatesProvider
            .getTemplateAccountBasisAndLegalFormExists$(
                this.template.accountBasis,
                this.template.legalForm,
                this.template.type,
                this.template._id,
            )
            .pipe(take(1))
            .subscribe((templateAccountBasisAndLegalFormExists: boolean) => {
                if (templateAccountBasisAndLegalFormExists) {
                    this.templateAccountBasisControl.setErrors({ templateAccountBasisAndLegalFormExists: true });
                    this.templateLegalFormControl.setErrors({ templateAccountBasisAndLegalFormExists: true });
                }
            });
    }

    validateTemplateForm() {
        this.template.name = this.template.name.trim();
        this.checkTemplateNameExists();
        this.checkTemplateAccountBasisAndLegalFormExists();
    }

    isValidTemplateForm(): boolean {
        let isValidTemplateForm =
            this.templateNameControl.valid &&
            this.templateAccountBasisControl.valid &&
            this.templateLegalFormControl.valid;
        if (this.template.type == TemplateType.BALANCE) {
            isValidTemplateForm = isValidTemplateForm && !!this.template.cashAndCash && !!this.template.profitOfTheYear;
        }
        return isValidTemplateForm;
    }

    save() {
        this.validateTemplateForm();
        if (this.isValidTemplateForm() && this.isValidTemplateGroup(this.template.group)) {
            const templateWithNamedSortedItems = cloneDeep(this.template);
            templateWithNamedSortedItems.group = SortingTemplateItemsHelper.mapGroupSortedItemsToItemIdType(
                cloneDeep(this.template.group),
            );
            if (this.isEditMode) {
                this.templatesProvider.updateTemplate(templateWithNamedSortedItems, templateWithNamedSortedItems._id);
            } else {
                this.templatesProvider.createTemplate(templateWithNamedSortedItems);
            }
        }
    }

    directToTemplatesList() {
        this.directToTemplatesListEvent.emit();
    }

    override ngOnDestroy() {
        this.templatesProvider.removeActiveTemplate();
        this.subscriptions.forEach((subscriber: Subscription) => {
            subscriber.unsubscribe();
        });
        super.ngOnDestroy();
    }

    onRemoveGroupTemplate(groupToBeRemoved: ITemplateGroupEntityDTO): void {
        this.equityGroupOptions = this.equityGroupOptions.filter(
            (groupName) => groupName !== groupToBeRemoved.groupRef.name,
        );
    }

    onAddGroupTemplate(groupToBeAdded: ITemplateGroupEntityDTO): void {
        this.equityGroupOptions = [...this.equityGroupOptions, groupToBeAdded.groupRef.name];
    }
}
